import React, { useState, useCallback, useEffect } from 'react';
import {
  Steps, Button, Upload, message, Card, Table, Select, Input,
  Form, Result, Alert, Tag, Typography, Divider, Progress,
  Statistic, Row, Col, Modal, Spin, Empty
} from 'antd';
import {
  UploadOutlined, FileExcelOutlined, CheckCircleOutlined,
  ArrowRightOutlined, SwapOutlined, CloseCircleOutlined,
  ClockCircleOutlined, ThunderboltOutlined,
  DatabaseOutlined, WarningOutlined, SyncOutlined,
  DeleteOutlined, HistoryOutlined, ExclamationCircleOutlined
} from '@ant-design/icons';
import { usePageHeader } from '../../layouts/AdminLayout';
import DataImportService from '../../services/DataImportService';
import EventTypeService from '../../services/EventTypeService';

const { Title, Text } = Typography;
const { Option, OptGroup } = Select;

const DataImport = () => {
  const { setPageHeader } = usePageHeader();
  const [currentStep, setCurrentStep] = useState(0);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  // Preview data from backend
  const [previewData, setPreviewData] = useState(null);

  // User's field mapping: { "Excel Header": "systemFieldKey" }
  const [mapping, setMapping] = useState({});

  // Event configuration
  const [eventConfig, setEventConfig] = useState({
    eventName: '',
    eventTypeId: '',
    location: '',
    city: '',
    state: '',
  });

  // Event types from the database
  const [eventTypes, setEventTypes] = useState([]);

  // Import results
  const [importResults, setImportResults] = useState(null);

  // Progress tracking
  const [importProgress, setImportProgress] = useState(null);
  const [importing, setImporting] = useState(false);

  // Import history
  const [importHistory, setImportHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setPageHeader({
      breadcrumbs: [
        { label: 'Home', path: '/admin/dashboard' },
        { label: 'Data Import' },
      ],
    });
  }, [setPageHeader]);

  // Load event types from database
  useEffect(() => {
    const loadEventTypes = async () => {
      try {
        const result = await EventTypeService.getEventTypes();
        if (result.success && result.data) {
          setEventTypes(Array.isArray(result.data) ? result.data : result.data.eventTypes || []);
        }
      } catch (err) {
        console.error('Failed to load event types:', err);
      }
    };
    loadEventTypes();
  }, []);

  // Load import history
  const loadImportHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const result = await DataImportService.getHistory();
      if (result.success) {
        setImportHistory(result.data || []);
      }
    } catch (err) {
      console.error('Failed to load import history:', err);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    loadImportHistory();
  }, [loadImportHistory]);

  // Delete imported data
  const handleDeleteImported = useCallback((batchId = null) => {
    const batchLabel = batchId ? 'this import batch' : 'ALL imported data';
    Modal.confirm({
      title: `Delete ${batchLabel}?`,
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>This will permanently delete:</p>
          <ul style={{ paddingLeft: 20 }}>
            <li>All imported patients{batchId ? ' from this batch' : ''}</li>
            <li>Their visits, screening records, and consents</li>
            <li>Associated bookings, time slots, and events</li>
          </ul>
          <Alert message="This action cannot be undone." type="error" showIcon style={{ marginTop: 12 }} />
        </div>
      ),
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        setDeleting(true);
        try {
          const result = await DataImportService.deleteImported(batchId);
          if (result.success) {
            const d = result.deleted;
            message.success(
              `Deleted ${d.patients} patients, ${d.visits} visits, ${d.screeningRecords} screening records, ${d.bookings} bookings, ${d.events} events`
            );
            loadImportHistory();
          }
        } catch (err) {
          message.error(err.message || 'Failed to delete imported data');
        } finally {
          setDeleting(false);
        }
      },
    });
  }, [loadImportHistory]);

  // ── Step 1: File Upload ──
  const handleFileSelect = useCallback(async (info) => {
    const selectedFile = info.file;
    setFile(selectedFile);
    setLoading(true);

    try {
      const result = await DataImportService.previewFile(selectedFile);
      setPreviewData(result.data);
      setMapping(result.data.suggestedMappings || {});

      const nameFromFile = selectedFile.name
        .replace(/\.(xlsx|csv|xls)$/i, '')
        .replace(/[_-]/g, ' ')
        .replace(/\d{4}.*$/, '')
        .trim();
      setEventConfig(prev => ({ ...prev, eventName: prev.eventName || nameFromFile }));

      setCurrentStep(1);
      message.success(`Parsed ${result.data.totalRows} rows from ${selectedFile.name}`);
    } catch (err) {
      message.error(err.message || 'Failed to parse file');
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Step 2: Event Setup ──
  const handleEventConfigChange = (field, value) => {
    setEventConfig(prev => ({ ...prev, [field]: value }));
  };

  // ── Step 3: Field Mapping ──
  const handleMappingChange = (excelHeader, systemField) => {
    setMapping(prev => {
      const next = { ...prev };
      if (systemField) {
        next[excelHeader] = systemField;
      } else {
        delete next[excelHeader];
      }
      return next;
    });
  };

  // ── Step 4: Execute with progress ──
  const handleExecuteImport = async () => {
    if (!file || !previewData) return;
    setImporting(true);
    setImportProgress({ current: 0, total: previewData.totalRows, created: 0, skipped: 0, errorCount: 0, estimatedSecondsLeft: 0 });

    try {
      const config = { ...eventConfig };

      const result = await DataImportService.executeImport(file, mapping, config, (progress) => {
        setImportProgress(progress);
      });

      setImportResults(result.data);
      setCurrentStep(4);
      message.success('Import completed!');
    } catch (err) {
      message.error(err.message || 'Import failed');
    } finally {
      setImporting(false);
      setImportProgress(null);
    }
  };

  // Group system fields by category for the dropdown
  const groupedFields = {};
  if (previewData?.systemFields) {
    for (const field of previewData.systemFields) {
      if (!groupedFields[field.category]) groupedFields[field.category] = [];
      groupedFields[field.category].push(field);
    }
  }

  const usedSystemFields = new Set(Object.values(mapping));
  const canProceedToMapping = eventConfig.eventName && eventConfig.eventTypeId;
  const mappedCount = Object.values(mapping).filter(v => v && v !== '_skip').length;
  const canProceedToReview = mappedCount > 0;

  const formatEta = (seconds) => {
    if (!seconds || seconds <= 0) return 'Almost done...';
    if (seconds < 60) return `~${seconds}s remaining`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `~${mins}m ${secs}s remaining`;
  };

  const stepItems = [
    { title: 'Upload File' },
    { title: 'Event Setup' },
    { title: 'Map Fields' },
    { title: 'Review & Import' },
    { title: 'Done' },
  ];

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      <Steps
        current={currentStep}
        items={stepItems}
        style={{ marginBottom: 32 }}
        size="small"
      />

      {/* ════════════════ STEP 0: Upload ════════════════ */}
      {currentStep === 0 && (
        <>
        <Card
          bordered={false}
          style={{ borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}
        >
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <div style={{
              width: 96, height: 96, borderRadius: '50%', margin: '0 auto 24px',
              background: 'linear-gradient(135deg, #e6f7e6 0%, #f0f9ff 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <FileExcelOutlined style={{ fontSize: 42, color: '#52c41a' }} />
            </div>
            <Title level={3} style={{ marginBottom: 8 }}>Upload Historical Data</Title>
            <Text type="secondary" style={{ display: 'block', marginBottom: 32, maxWidth: 480, margin: '0 auto 32px' }}>
              Upload an Excel (.xlsx) or CSV file containing member screening data.
              You&apos;ll map columns to system fields in the next steps.
            </Text>
            <Upload
              accept=".xlsx,.xls,.csv"
              showUploadList={false}
              beforeUpload={() => false}
              onChange={handleFileSelect}
              maxCount={1}
            >
              <Button
                type="primary"
                size="large"
                icon={<UploadOutlined />}
                loading={loading}
                style={{ height: 48, paddingInline: 32, borderRadius: 8, fontSize: 16 }}
              >
                Select File
              </Button>
            </Upload>
          </div>
        </Card>

        {/* Import History */}
        <Card
          bordered={false}
          style={{ borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', marginTop: 24 }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <HistoryOutlined style={{ fontSize: 18, color: '#1890ff' }} />
              <Title level={5} style={{ margin: 0 }}>Import History</Title>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {importHistory.length > 0 && (
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleDeleteImported()}
                  loading={deleting}
                >
                  Delete All Imported
                </Button>
              )}
              <Button
                icon={<SyncOutlined />}
                onClick={loadImportHistory}
                loading={historyLoading}
              >
                Refresh
              </Button>
            </div>
          </div>

          {historyLoading && !importHistory.length ? (
            <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
          ) : importHistory.length === 0 ? (
            <Empty description="No imports yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <Table
              dataSource={importHistory.map((h, i) => ({ key: i, ...h }))}
              columns={[
                {
                  title: 'Date',
                  dataIndex: 'timestamp',
                  key: 'date',
                  width: 180,
                  render: (ts) => new Date(ts).toLocaleString(),
                },
                {
                  title: 'Batch ID',
                  dataIndex: 'importBatchId',
                  key: 'batch',
                  width: 200,
                  render: (id) => <Text copyable style={{ fontSize: 12 }}>{id?.substring(0, 8)}...</Text>,
                },
                {
                  title: 'Details',
                  dataIndex: 'details',
                  key: 'details',
                  render: (details) => {
                    if (!details) return '-';
                    return (
                      <div>
                        {details.fileName && <Text style={{ marginRight: 12 }}>{details.fileName}</Text>}
                        {details.totalRows != null && <Tag>{details.totalRows} rows</Tag>}
                        {details.created != null && <Tag color="green">{details.created} created</Tag>}
                        {details.updated != null && <Tag color="blue">{details.updated} updated</Tag>}
                        {details.skipped != null && details.skipped > 0 && <Tag color="orange">{details.skipped} skipped</Tag>}
                      </div>
                    );
                  },
                },
                {
                  title: 'Imported By',
                  dataIndex: 'importedBy',
                  key: 'user',
                  width: 150,
                  render: (user) => user ? (user.fullName || user.email || '-') : '-',
                },
                {
                  title: '',
                  key: 'actions',
                  width: 100,
                  render: (_, record) => (
                    <Button
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleDeleteImported(record.importBatchId)}
                      loading={deleting}
                    >
                      Delete
                    </Button>
                  ),
                },
              ]}
              pagination={false}
              size="small"
            />
          )}
        </Card>
        </>
      )}

      {/* ════════════════ STEP 1: Event Setup ════════════════ */}
      {currentStep === 1 && previewData && (
        <Card
          bordered={false}
          style={{ borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}
        >
          <div style={{
            background: 'linear-gradient(135deg, #f0f5ff 0%, #e6fffb 100%)',
            borderRadius: 8, padding: '16px 20px', marginBottom: 24,
            display: 'flex', alignItems: 'center', gap: 12
          }}>
            <FileExcelOutlined style={{ fontSize: 20, color: '#1890ff' }} />
            <div>
              <Text strong>{previewData.fileName}</Text>
              <Text type="secondary" style={{ marginLeft: 12 }}>
                {previewData.totalRows.toLocaleString()} rows &middot; {previewData.headers.length} columns
              </Text>
            </div>
          </div>

          <Title level={5} style={{ marginBottom: 16 }}>Event Configuration</Title>
          <Form layout="vertical" style={{ maxWidth: 560 }}>
            <Form.Item label="Event Name" required>
              <Input
                value={eventConfig.eventName}
                onChange={e => handleEventConfigChange('eventName', e.target.value)}
                placeholder="e.g. DFW Bus Screening 2024"
                size="large"
              />
            </Form.Item>
            <Form.Item label="Event Type" required>
              <Select
                value={eventConfig.eventTypeId || undefined}
                onChange={v => {
                  handleEventConfigChange('eventTypeId', v);
                  const selected = eventTypes.find(et => et._id === v);
                  if (selected) {
                    if (selected.city) handleEventConfigChange('city', selected.city);
                    if (selected.state) handleEventConfigChange('state', selected.state);
                  }
                }}
                placeholder="Select an event type"
                showSearch
                optionFilterProp="children"
                size="large"
              >
                {eventTypes.map(et => (
                  <Option key={et._id} value={et._id}>
                    {et.name}{et.city ? ` — ${et.city}, ${et.state}` : ''}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item label="Location">
              <Input
                value={eventConfig.location}
                onChange={e => handleEventConfigChange('location', e.target.value)}
                placeholder="e.g. Dallas community center"
              />
            </Form.Item>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="City">
                  <Input
                    value={eventConfig.city}
                    onChange={e => handleEventConfigChange('city', e.target.value)}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="State">
                  <Input
                    value={eventConfig.state}
                    onChange={e => handleEventConfigChange('state', e.target.value)}
                    maxLength={2}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
          <Divider style={{ margin: '16px 0' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button onClick={() => setCurrentStep(0)}>Back</Button>
            <Button
              type="primary"
              disabled={!canProceedToMapping}
              onClick={() => setCurrentStep(2)}
              style={{ borderRadius: 6 }}
            >
              Next: Map Fields <ArrowRightOutlined />
            </Button>
          </div>
        </Card>
      )}

      {/* ════════════════ STEP 2: Field Mapping ════════════════ */}
      {currentStep === 2 && previewData && (
        <Card
          bordered={false}
          style={{ borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <Title level={5} style={{ margin: 0 }}>Field Mapping</Title>
            <Tag color={mappedCount > 0 ? 'green' : 'default'} style={{ fontSize: 13, padding: '2px 12px' }}>
              {mappedCount} / {previewData.headers.length} mapped
            </Tag>
          </div>

          <Alert
            message="Map each Excel column to a system field. Unmapped columns will be skipped."
            type="info"
            showIcon
            style={{ marginBottom: 16, borderRadius: 8 }}
          />
          <Table
            dataSource={previewData.headers.map((header, idx) => ({
              key: idx,
              header,
              sampleValues: previewData.sampleRows
                .slice(0, 3)
                .map(r => r[header])
                .filter(v => v !== '' && v !== null && v !== undefined)
                .map(v => String(v).substring(0, 40))
            }))}
            columns={[
              {
                title: 'Excel Column',
                dataIndex: 'header',
                key: 'header',
                width: 180,
                render: (text) => <Text strong style={{ fontSize: 13 }}>{text}</Text>,
              },
              {
                title: 'Sample Data',
                dataIndex: 'sampleValues',
                key: 'sample',
                width: 220,
                render: (vals) => (
                  <div>
                    {vals.map((v, i) => (
                      <Text key={i} type="secondary" style={{ fontSize: 12, display: 'block', lineHeight: '18px' }}>{v}</Text>
                    ))}
                  </div>
                ),
              },
              {
                title: '',
                key: 'arrow',
                width: 40,
                align: 'center',
                render: () => <SwapOutlined style={{ color: '#bbb', fontSize: 16 }} />,
              },
              {
                title: 'System Field',
                key: 'mapping',
                width: 280,
                render: (_, record) => {
                  const currentValue = mapping[record.header] || undefined;
                  return (
                    <Select
                      value={currentValue}
                      onChange={v => handleMappingChange(record.header, v)}
                      placeholder="— Skip —"
                      allowClear
                      style={{ width: '100%' }}
                      showSearch
                      optionFilterProp="children"
                      size="middle"
                    >
                      {Object.entries(groupedFields).map(([category, fields]) => (
                        <OptGroup key={category} label={category}>
                          {fields.map(f => (
                            <Option
                              key={f.key}
                              value={f.key}
                              disabled={usedSystemFields.has(f.key) && mapping[record.header] !== f.key}
                            >
                              {f.label}
                              {f.transform && <Tag color="blue" style={{ marginLeft: 8, fontSize: 10 }}>auto-parse</Tag>}
                            </Option>
                          ))}
                        </OptGroup>
                      ))}
                    </Select>
                  );
                },
              },
              {
                title: '',
                key: 'status',
                width: 50,
                align: 'center',
                render: (_, record) => {
                  const val = mapping[record.header];
                  if (val && val !== '_skip') {
                    return <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 18 }} />;
                  }
                  return <CloseCircleOutlined style={{ color: '#d9d9d9', fontSize: 18 }} />;
                },
              },
            ]}
            pagination={false}
            size="small"
            style={{ marginBottom: 16 }}
          />
          <Divider style={{ margin: '16px 0' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button onClick={() => setCurrentStep(1)}>Back</Button>
            <Button
              type="primary"
              disabled={!canProceedToReview}
              onClick={() => setCurrentStep(3)}
              style={{ borderRadius: 6 }}
            >
              Next: Review <ArrowRightOutlined />
            </Button>
          </div>
        </Card>
      )}

      {/* ════════════════ STEP 3: Review & Execute ════════════════ */}
      {currentStep === 3 && previewData && (
        <Card
          bordered={false}
          style={{ borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', position: 'relative', overflow: 'hidden' }}
        >
          {/* Importing overlay */}
          {importing && importProgress && (
            <div style={{
              position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.97)',
              zIndex: 10, borderRadius: 12, display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', padding: 40
            }}>
              <ThunderboltOutlined style={{ fontSize: 36, color: '#1890ff', marginBottom: 16 }} />
              <Title level={4} style={{ marginBottom: 4 }}>Importing Records...</Title>
              <Text type="secondary" style={{ marginBottom: 24 }}>
                {formatEta(importProgress.estimatedSecondsLeft)}
              </Text>

              <div style={{ width: '100%', maxWidth: 480 }}>
                <Progress
                  percent={Math.round((importProgress.current / importProgress.total) * 100)}
                  strokeColor={{ '0%': '#108ee9', '100%': '#87d068' }}
                  strokeWidth={12}
                  style={{ marginBottom: 24 }}
                />

                <Row gutter={12} style={{ textAlign: 'center' }}>
                  <Col span={5}>
                    <Statistic
                      title="Processed"
                      value={importProgress.current}
                      suffix={<Text type="secondary" style={{ fontSize: 12 }}>/ {importProgress.total}</Text>}
                      valueStyle={{ fontSize: 20 }}
                    />
                  </Col>
                  <Col span={5}>
                    <Statistic
                      title="Created"
                      value={importProgress.created}
                      valueStyle={{ color: '#52c41a', fontSize: 20 }}
                      prefix={<DatabaseOutlined />}
                    />
                  </Col>
                  <Col span={5}>
                    <Statistic
                      title="Updated"
                      value={importProgress.updated || 0}
                      valueStyle={{ color: '#1890ff', fontSize: 20 }}
                      prefix={<SyncOutlined />}
                    />
                  </Col>
                  <Col span={5}>
                    <Statistic
                      title="Skipped"
                      value={importProgress.skipped}
                      valueStyle={{ color: '#faad14', fontSize: 20 }}
                    />
                  </Col>
                  <Col span={4}>
                    <Statistic
                      title="Errors"
                      value={importProgress.errorCount}
                      valueStyle={{ color: importProgress.errorCount > 0 ? '#ff4d4f' : '#52c41a', fontSize: 20 }}
                      prefix={importProgress.errorCount > 0 ? <WarningOutlined /> : null}
                    />
                  </Col>
                </Row>
              </div>
            </div>
          )}

          {/* Summary header */}
          <div style={{
            background: 'linear-gradient(135deg, #f6ffed 0%, #e6f7ff 100%)',
            borderRadius: 8, padding: '16px 20px', marginBottom: 24,
            display: 'flex', alignItems: 'center', gap: 12
          }}>
            <CheckCircleOutlined style={{ fontSize: 20, color: '#52c41a' }} />
            <div>
              <Text strong style={{ fontSize: 15 }}>{eventConfig.eventName}</Text>
              <Tag color="blue" style={{ marginLeft: 8 }}>
                {eventTypes.find(et => et._id === eventConfig.eventTypeId)?.name || 'Unknown'}
              </Tag>
              {eventConfig.city && (
                <Text type="secondary" style={{ marginLeft: 4 }}>
                  {eventConfig.city}{eventConfig.state ? `, ${eventConfig.state}` : ''}
                </Text>
              )}
            </div>
          </div>

          {/* Stats row */}
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={8}>
              <Card size="small" bordered style={{ borderRadius: 8, textAlign: 'center' }}>
                <Statistic title="File" value={previewData.fileName} valueStyle={{ fontSize: 14 }} />
              </Card>
            </Col>
            <Col span={8}>
              <Card size="small" bordered style={{ borderRadius: 8, textAlign: 'center' }}>
                <Statistic title="Total Rows" value={previewData.totalRows} valueStyle={{ color: '#1890ff' }} />
              </Card>
            </Col>
            <Col span={8}>
              <Card size="small" bordered style={{ borderRadius: 8, textAlign: 'center' }}>
                <Statistic
                  title="Mapped Columns"
                  value={mappedCount}
                  suffix={`/ ${previewData.headers.length}`}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Mapping table */}
          <Title level={5} style={{ marginBottom: 12 }}>Field Mappings</Title>
          <Table
            dataSource={Object.entries(mapping)
              .filter(([, v]) => v && v !== '_skip')
              .map(([header, field], idx) => {
                const sysField = previewData.systemFields.find(f => f.key === field);
                return { key: idx, header, field, label: sysField?.label || field, category: sysField?.category || '' };
              })}
            columns={[
              { title: 'Excel Column', dataIndex: 'header', key: 'h', render: t => <Text code>{t}</Text> },
              { title: 'System Field', dataIndex: 'label', key: 'l', render: t => <Text strong>{t}</Text> },
              { title: 'Category', dataIndex: 'category', key: 'c', render: c => <Tag>{c}</Tag> },
            ]}
            pagination={false}
            size="small"
            style={{ marginBottom: 24 }}
          />

          <Alert
            message={
              <span>
                <ClockCircleOutlined style={{ marginRight: 8 }} />
                Importing {previewData.totalRows.toLocaleString()} rows may take a few minutes. You&apos;ll see a live progress bar.
              </span>
            }
            type="warning"
            style={{ marginBottom: 24, borderRadius: 8 }}
          />

          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button onClick={() => setCurrentStep(2)} disabled={importing}>Back</Button>
            <Button
              type="primary"
              size="large"
              onClick={handleExecuteImport}
              loading={importing}
              disabled={importing}
              style={{ height: 48, paddingInline: 32, borderRadius: 8, fontSize: 16 }}
            >
              {importing ? 'Importing...' : `Import ${previewData.totalRows.toLocaleString()} Rows`}
            </Button>
          </div>
        </Card>
      )}

      {/* ════════════════ STEP 4: Results ════════════════ */}
      {currentStep === 4 && importResults && (
        <Card
          bordered={false}
          style={{ borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}
        >
          <Result
            status="success"
            title="Import Complete"
            subTitle={<Text type="secondary">Batch ID: <Text code>{importResults.importBatchId}</Text></Text>}
            extra={[
              <Button
                key="new"
                type="primary"
                style={{ borderRadius: 6 }}
                onClick={() => {
                  setCurrentStep(0);
                  setFile(null);
                  setPreviewData(null);
                  setMapping({});
                  setImportResults(null);
                  setEventConfig({ eventName: '', eventTypeId: '', location: '', city: '', state: '' });
                }}
              >
                Import Another File
              </Button>,
            ]}
          />

          <Row gutter={16} style={{ maxWidth: 600, margin: '0 auto' }}>
            <Col span={6}>
              <Card size="small" bordered style={{ borderRadius: 8, textAlign: 'center' }}>
                <Statistic title="Created" value={importResults.results.created} valueStyle={{ color: '#52c41a' }} />
              </Card>
            </Col>
            <Col span={6}>
              <Card size="small" bordered style={{ borderRadius: 8, textAlign: 'center' }}>
                <Statistic title="Updated" value={importResults.results.updated} valueStyle={{ color: '#1890ff' }} />
              </Card>
            </Col>
            <Col span={6}>
              <Card size="small" bordered style={{ borderRadius: 8, textAlign: 'center' }}>
                <Statistic title="Skipped" value={importResults.results.skipped} valueStyle={{ color: '#faad14' }} />
              </Card>
            </Col>
            <Col span={6}>
              <Card size="small" bordered style={{ borderRadius: 8, textAlign: 'center' }}>
                <Statistic
                  title="Errors"
                  value={importResults.results.errors.length}
                  valueStyle={{ color: importResults.results.errors.length > 0 ? '#ff4d4f' : '#52c41a' }}
                />
              </Card>
            </Col>
          </Row>

          {importResults.results.errors.length > 0 && (
            <div style={{ maxWidth: 600, margin: '24px auto 0' }}>
              <Title level={5}>Errors</Title>
              <Table
                dataSource={importResults.results.errors.map((e, i) => ({ key: i, ...e }))}
                columns={[
                  { title: 'Row', dataIndex: 'row', key: 'row', width: 60 },
                  { title: 'Error', dataIndex: 'message', key: 'msg' },
                ]}
                pagination={{ pageSize: 10 }}
                size="small"
              />
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

export default DataImport;
